import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { type SequenceStepInput } from './sequence'

export const SEQUENCE_TEMPLATES_PATH = '/insure/internal/sequence-templates'

export interface SequenceTemplateOwner {
  id: string
  name: string
}

export interface SequenceTemplate {
  id: string
  name: string
  /** Shared with every staff user; `owner` is then its author. */
  isGlobal: boolean
  steps: SequenceStepInput[]
  owner: SequenceTemplateOwner
  createdAt: string
  updatedAt: string
}

type SequenceTemplateInput = Pick<SequenceTemplate, 'name' | 'isGlobal' | 'steps'>

const BASE = '/api/v1/insure/internal/sequence-templates'

function listTemplates(): Promise<SequenceTemplate[]> {
  return axios.get(`${BASE}/`).then((r) => toCamelCase(r.data) as SequenceTemplate[])
}

function createTemplate(data: SequenceTemplateInput): Promise<SequenceTemplate> {
  return axios.post(`${BASE}/`, toSnakeCase(data)).then((r) => toCamelCase(r.data) as SequenceTemplate)
}

function updateTemplate(
  id: string,
  data: Partial<Pick<SequenceTemplate, 'name' | 'steps'>>
): Promise<SequenceTemplate> {
  return axios.patch(`${BASE}/${id}/`, toSnakeCase(data)).then((r) => toCamelCase(r.data) as SequenceTemplate)
}

function deleteTemplate(id: string): Promise<void> {
  return axios.delete(`${BASE}/${id}/`).then(() => undefined)
}

/** Global templates plus the rep's own, by name: everything a sequence builder offers. */
export function useSequenceTemplates() {
  return useQuery({
    queryKey: [CacheKeys.SEQUENCE_TEMPLATES],
    queryFn: listTemplates,
  })
}

export function useCreateSequenceTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.SEQUENCE_TEMPLATES] }),
  })
}

export function useUpdateSequenceTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Pick<SequenceTemplate, 'name' | 'steps'>>) =>
      updateTemplate(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.SEQUENCE_TEMPLATES] }),
  })
}

export function useDeleteSequenceTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CacheKeys.SEQUENCE_TEMPLATES] }),
  })
}
